import {
  useCallback,
  useEffect,
  useState,
  useMemo,
  type SetStateAction,
} from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { images } from "@img/imagesMap";
import type { NurseWithRelation } from "@vTypes/nurse-type";
import { convertAvailableMembersToNurseWithRelation } from "@utils/nurse-util";
import CustomModal from "@/components/modal/CustomModal";
import { useIsAdmin, useLoginUser } from "@hooks/useAuth";
import { MSG } from "@constants/MSG.ts";
import { toast } from "@/components/toast/CustomToast";
import { ManagerTable } from "@/pages/roster-management/components/ManagerTable";
import { QUERY_BASE_KEY } from "@constants/query-key";
import { compareNurses } from "@/utils/sort-util";
import { COLUMN_WIDTHS } from "@/pages/roster-management/components/rosterTable.config";
import useAvailableMember from "@/hooks/useAvailbleMember";
import { AvailableMemberTable } from "./AvailableMemberTable";
import type { AvailableMember } from "@/hooks/useAvailbleMember";
import httpClient from "@/config/axios";
import { useGroupSelection } from "@/hooks/useGroupSelection";
import { ChevronUp } from "lucide-react";
import NurseModalFooterButtons from "./NurseModalFooterButtons";

export default function NurseAddContents({
  onClose,
  selectedGroupId,
}: {
  onClose: () => void;
  selectedGroupId: string;
}) {
  const [isOpen, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const loginUser = useLoginUser();
  const isAdmin = useIsAdmin();

  const { groups } = useGroupSelection();
  const groupName =
    groups?.find((g) => g.group_id === selectedGroupId)?.group_name ||
    "선택된 그룹";

  // 메디통 병원 근무자
  const { data: availableMemberData, isError, error } = useAvailableMember();

  useEffect(() => {
    if (isError) {
      console.error("메디통 병원 근무자 조회 오류:", error);
    }
  }, [isError, error]);

  // 활성/비활성 리스트 상태
  const [activeList, setActiveList] = useState<NurseWithRelation[]>([]);
  // const [inactiveList, setInactiveList] = useState<NurseWithRelation[]>([]);
  // 메디통 병원 근무자 리스트 (원본 데이터)
  const [availableMemberList, setAvailableMemberList] = useState<
    AvailableMember[]
  >([]);

  // 메디통 병원 근무자 데이터 초기화
  useEffect(() => {
    if (availableMemberData) {
      setAvailableMemberList(availableMemberData);
    }
  }, [availableMemberData]);

  // 검색 관련 상태
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [availableSearchTerm, setAvailableSearchTerm] = useState<string>("");
  const [highlightedNurseId, setHighlightedNurseId] = useState<string | null>(
    null
  );

  // 메디통 병원 근무자 선택 상태
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(
    new Set()
  );

  const handleToggleSelection = (nurseId: string) => {
    setSelectedMembers((prev) => {
      const next = new Set(prev);
      next.has(nurseId) ? next.delete(nurseId) : next.add(nurseId);
      return next;
    });
  };

  const handleRangeSelection = (nurseIds: string[]) => {
    setSelectedMembers((prev) => {
      const next = new Set(prev);
      // 범위 내 모두 선택 (이미 선택된 것도 유지)
      nurseIds.forEach((id) => next.add(id));
      return next;
    });
  };

  // 추가하기 버튼 핸들러
  const handleAddMembers = useCallback(() => {
    const selectedMemberList = availableMemberList.filter((m) =>
      selectedMembers.has(m.nurse_id)
    );

    if (selectedMemberList.length === 0) {
      toast.warning("추가할 근무자를 선택해주세요.");
      return;
    }

    const groupId = isAdmin ? selectedGroupId : loginUser?.group_id || "";
    const officeId = loginUser?.office_id ?? "";
    const converted = convertAvailableMembersToNurseWithRelation(
      selectedMemberList,
      groupId,
      officeId
    ).map((n) => ({ ...n, active: 1 }));

    // 중복 체크
    const existingIds = new Set(activeList.map((n) => n.nurse_id));
    const newNurses = converted.filter((n) => !existingIds.has(n.nurse_id));

    if (newNurses.length === 0) {
      toast.warning("이미 이동된 근무자입니다.");
      return;
    }

    setActiveList((prev) => [...prev, ...newNurses]);
    setAvailableMemberList((prev) =>
      prev.filter((m) => !selectedMembers.has(m.nurse_id))
    );
    setSelectedMembers(new Set());

    toast.success(`${newNurses.length}명의 근무자가 이동되었습니다.`);
  }, [
    selectedMembers,
    availableMemberList,
    activeList,
    isAdmin,
    selectedGroupId,
    loginUser,
  ]);

  // 정렬 상태 (로컬 정렬만, 서버 저장 안 함)
  const [sortConfig, setSortConfig] = useState<{
    key: "experience" | "grade" | "name" | null;
    order: "asc" | "desc" | null;
  }>({ key: null, order: null });

  // 정렬 핸들러
  const handleSort = useCallback((key: "experience" | "grade" | "name") => {
    setSortConfig((prev) => {
      const isSameKey = prev.key === key;
      let nextOrder: "asc" | "desc" | null = "asc";
      if (isSameKey) {
        if (prev.order === "asc") nextOrder = "desc";
        else if (prev.order === "desc") nextOrder = null;
      }

      const nextConfig = { key: nextOrder ? key : null, order: nextOrder };

      if (nextConfig.key && nextConfig.order) {
        const { key: sortKey, order } = nextConfig;
        const sorter = (a: NurseWithRelation, b: NurseWithRelation) =>
          compareNurses(a, b, sortKey, order);

        setActiveList((prev) => [...prev].sort(sorter));
      }

      return nextConfig;
    });
  }, []);

  // 저장 Mutation
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => {
      const nurseIds = activeList.map((n) => n.nurse_id);
      const targetGroupId = isAdmin
        ? selectedGroupId
        : loginUser?.group_id || "";

      if (!targetGroupId) {
        throw new Error("그룹 ID가 없습니다.");
      }

      await httpClient.post("/nurses/add-to-group", {
        nurse_ids: nurseIds,
        group_id: targetGroupId,
      });
    },
    onSuccess: () => {
      toast.success("근무자 추가가 완료되었습니다.");
      setOpen(false);
      const groupId = isAdmin ? selectedGroupId : "";
      queryClient.invalidateQueries({
        queryKey: [QUERY_BASE_KEY.NURSES, groupId],
      });
      onClose();
    },
  });

  // 저장 버튼 클릭
  const handleSaveClick = () => {
    setOpen(true);
  };

  // 하이라이트된 요소로 스크롤
  useEffect(() => {
    if (highlightedNurseId) {
      setTimeout(() => {
        const element = document.querySelector(
          `[data-nid="${highlightedNurseId}"]`
        ) as HTMLElement;

        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
    }
  }, [highlightedNurseId]);

  // 검색 실행 (선택된 근무자)
  const handleSearch = useCallback(() => {
    if (!searchTerm.trim()) {
      setHighlightedNurseId(null);
      return;
    }

    const foundActive = activeList.find((n) => n.name === searchTerm.trim());

    if (foundActive) {
      setHighlightedNurseId(foundActive.nurse_id);
      toast.info(`'${foundActive.name}' 간호사를 찾았습니다.`);
    } else {
      setHighlightedNurseId(null);
      toast.error(`'${searchTerm}' 검색 결과가 없습니다.`);
    }
  }, [searchTerm, activeList]);

  // 검색 실행 (메디통 병원 근무자)
  const handleAvailableSearch = useCallback(() => {
    if (!availableSearchTerm.trim()) {
      setHighlightedNurseId(null);
      return;
    }

    const foundAvailable = availableMemberList.find(
      (m) => m.name === availableSearchTerm.trim()
    );

    if (foundAvailable) {
      setHighlightedNurseId(foundAvailable.nurse_id);
      toast.info(`'${foundAvailable.name}' 간호사를 찾았습니다.`);
    } else {
      setHighlightedNurseId(null);
      toast.error(`'${availableSearchTerm}' 검색 결과가 없습니다.`);
    }
  }, [availableSearchTerm, availableMemberList]);

  // 엔터키 검색
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleAvailableSearchKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      handleAvailableSearch();
    }
  };

  // 근무자 추가 모달용 커스텀 헤더 리스트 (No. 사번 이름 직종 나이(만) 경력 입사일만 표시)
  const customHeaderList = useMemo(() => {
    return [
      { label: "", width: "0px" },
      { label: "No.", width: COLUMN_WIDTHS.no },
      { label: "사번", width: COLUMN_WIDTHS.empNum },
      { label: "이름", width: COLUMN_WIDTHS.name },
      { label: "직종", width: COLUMN_WIDTHS.role },
      { label: "성별", width: "60px" },
      { label: "나이(만)", width: COLUMN_WIDTHS.age },
      { label: "경력", width: COLUMN_WIDTHS.experience },
      { label: "입사일", width: COLUMN_WIDTHS.joiningDate },
      { label: "생년월일", width: COLUMN_WIDTHS.joiningDate },
      { label: "연락처", width: "120px" },
      { label: "소속", width: "100px" },
    ];
  }, []);

  // NurseWithRelation -> AvailableMember 변환 및 상태 업데이트
  const handleSetAvailableMemberList = (
    newList: SetStateAction<AvailableMember[]>
  ) => {
    const list =
      typeof newList === "function" ? newList(availableMemberList) : newList;

    const convertedList = (list as (AvailableMember | NurseWithRelation)[]).map(
      (item) => {
        // NurseWithRelation -> AvailableMember 변환
        // item에 role이 있고 duty가 없으면 NurseWithRelation으로 간주
        if ("role" in item && !("duty" in item)) {
          const nurse = item as NurseWithRelation & { affiliation?: string };

          return {
            ...nurse,
            duty: nurse.role ?? "RN",
            career: String(nurse.experience || 0),
            small_kind_name: nurse.affiliation || "",
            mb_part_name: "", // affiliation에 통합되어 있다고 가정
            // 날짜 변환 (Date -> string)
            joining_date: nurse.joining_date
              ? new Date(nurse.joining_date).toISOString()
              : "",
            birth_date: nurse.birth_date
              ? new Date(nurse.birth_date).toISOString()
              : "",
          } as unknown as AvailableMember;
        }

        return item as AvailableMember;
      }
    );

    setAvailableMemberList(convertedList);
  };

  return (
    <>
      <CustomModal.Confirm
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        onConfirm={mutateAsync}
        isPending={isPending}
        pendingMsg={MSG.ACTIVE_SETTING_SAVE}
        title="근무자 추가를 저장하시겠습니까?"
      />

      <div className="flex-1 min-h-0 flex-col pb-2">
        <div className="px-6 pt-[26px] pb-[24px]">
          {/* 상단 정보 및 검색 */}
          <div className="flex flex-col w-fit mx-auto px-4 min-w-[800px]">
            <div className="flex items-center justify-between mb-[25px]">
              <div className="w-[250px]"></div>
              <h2 className="text-[24px] font-pretendard font-bold text-sig text-center">
                근무자 추가
              </h2>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 relative">
                  <input
                    type="text"
                    placeholder="이름으로 검색"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    className="bg-white w-64 pl-4 pr-10 py-2 border border-[#D6D6DE] rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#0e59c6]"
                  />
                  <button
                    onClick={handleSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    <img
                      src={images.icon_search}
                      alt="search"
                      className="w-4 h-4"
                    />
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-8">
              {/* 선택된 근무자 리스트 */}
              <div className="flex flex-col">
                <h3 className="px-10 text-lg font-bold text-zinc-900 bg-[#C4C4C4] p-2 text-center rounded-t-lg grid grid-cols-3">
                  <div className="flex items-center gap-2">
                    <img
                      src={images.icon_person_black}
                      alt=""
                      className="size-3 "
                    />
                    <span className="font-pretendard text-[14px]">
                      {activeList.length > 0 ? activeList.length : 0}
                    </span>
                  </div>
                  <div>
                    <span className="font-pretendard font-bold text-[15px] text-text-main2">
                      선택된 근무자
                    </span>
                  </div>
                  <div></div>
                </h3>
                <ManagerTable
                  teamName={groupName}
                  nurseDataWithRelation={activeList ?? []}
                  selectedGroupId={selectedGroupId}
                  activeNurseIdForSideProfile={highlightedNurseId}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  disableRowEdit={true}
                  hideEditColumn={true}
                  customHeaderList={customHeaderList}
                  addNurseModal={true}
                  className="h-[295px] overflow-y-auto scrollbar-float"
                />
              </div>

              <div className="flex justify-between">
                <div className="w-[200px]"></div>
                <div className="flex">
                  <button
                    onClick={handleAddMembers}
                    className="flex justify-center items-center w-[140px] h-[45px] bg-[#0E59C6] text-white rounded-full hover:bg-[#0052CC] transition-colors gap-1"
                  >
                    <span className="font-pretendard font-semibold text-[16px] whitespace-nowrap mr-[5px]">
                      추가하기
                    </span>
                    <ChevronUp className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 relative">
                    <input
                      type="text"
                      placeholder="이름으로 검색"
                      value={availableSearchTerm}
                      onChange={(e) => setAvailableSearchTerm(e.target.value)}
                      onKeyDown={handleAvailableSearchKeyDown}
                      className="bg-white w-64 pl-4 pr-10 py-2 border border-[#D6D6DE] rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#0e59c6]"
                    />
                    <button
                      onClick={handleAvailableSearch}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      <img
                        src={images.icon_search}
                        alt="search"
                        className="w-4 h-4"
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* 메디통 병원 근무자 리스트 */}
              <div className="flex flex-col">
                <h3 className="px-10 text-lg font-bold text-zinc-900 bg-[#C4C4C4] p-2 text-center rounded-t-lg grid grid-cols-3 items-center">
                  <div className="flex items-center gap-2">
                    <img
                      src={images.icon_person_black}
                      alt=""
                      className="size-3 "
                    />
                    <span className="font-pretendard text-[14px]">
                      {availableMemberList.length > 0
                        ? availableMemberList.length
                        : ""}
                    </span>
                  </div>
                  <div>
                    <span className="font-pretendard text-[15px] text-text-main2">
                      메디통 병원 근무자
                    </span>
                  </div>
                </h3>

                <AvailableMemberTable
                  members={availableMemberList}
                  highlightedNurseId={highlightedNurseId}
                  selectedMembers={selectedMembers}
                  onToggleSelection={handleToggleSelection}
                  onRangeSelection={handleRangeSelection}
                  className="h-[295px] overflow-y-auto scrollbar-float"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <NurseModalFooterButtons onClose={onClose} onConfirm={handleSaveClick} />
    </>
  );
}
